/** Date-range helpers for dashboard presets, date filters and month pickers. */

export type RangePreset = 'week' | 'month' | 'quarter';

export const RANGE_PRESETS: Array<{ value: RangePreset; label: string; days: number }> = [
  { value: 'week', label: 'Last 7 days', days: 7 },
  { value: 'month', label: 'Last 30 days', days: 30 },
  { value: 'quarter', label: 'Last 90 days', days: 90 },
];

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

export function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/**
 * Range covering the last N days including today, in local time, as ISO
 * strings. `to` is the end of today so the value is stable during a day
 * (which keeps query cache keys stable).
 */
export function presetRange(preset: RangePreset, now: Date = new Date()): { from: string; to: string } {
  const days = RANGE_PRESETS.find((p) => p.value === preset)?.days ?? 7;
  const from = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1)));
  return { from: from.toISOString(), to: endOfDay(now).toISOString() };
}

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse "YYYY-MM-DD" as a local date, or null. */
export function parseDay(value: string | null | undefined): Date | null {
  const m = value ? DAY_RE.exec(value) : null;
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d : null;
}

/** "YYYY-MM-DD" (local) -> ISO timestamp at local start of that day. */
export function dayStartIso(day: string): string | undefined {
  const d = parseDay(day);
  return d ? startOfDay(d).toISOString() : undefined;
}

/** "YYYY-MM-DD" (local) -> ISO timestamp at local end of that day. */
export function dayEndIso(day: string): string | undefined {
  const d = parseDay(day);
  return d ? endOfDay(d).toISOString() : undefined;
}

const MONTH_RE = /^(\d{4})-(\d{2})$/;

export function isMonth(value: string | null | undefined): value is string {
  const m = value ? MONTH_RE.exec(value) : null;
  return !!m && Number(m[2]) >= 1 && Number(m[2]) <= 12;
}

/** "YYYY-MM" for the given date (local). */
export function monthOf(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** shiftMonth("2026-01", -1) -> "2025-12" */
export function shiftMonth(month: string, delta: number): string {
  const m = MONTH_RE.exec(month);
  if (!m) return month;
  return monthOf(new Date(Number(m[1]), Number(m[2]) - 1 + delta, 1));
}

/** "2026-09" -> "September 2026" */
export function formatMonth(month: string, locale?: string): string {
  const m = MONTH_RE.exec(month);
  if (!m) return month;
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    new Date(Number(m[1]), Number(m[2]) - 1, 1),
  );
}
